/**
 * LERA Device Engine — Device Index
 * Builds a global searchable dictionary and per-brand views from catalog data.
 */
(function (global) {
  'use strict';

  var LERA = (global.LERA = global.LERA || {});
  LERA._modules = LERA._modules || {};

  var debug = LERA._debug;
  var SCOPE = 'DeviceIndex';

  function getOptionName(optionEntry) {
    // Shopify's /collections/*/products.json returns options as objects
    // ({ name, position, values }); other sources may return plain strings.
    if (typeof optionEntry === 'string') return optionEntry;
    if (optionEntry && typeof optionEntry.name === 'string') return optionEntry.name;
    return '';
  }

  function getOptionIndex(product, optionName) {
    var normalizedTarget = String(optionName || '').trim().toLowerCase();
    var options = product.options || [];

    for (var index = 0; index < options.length; index += 1) {
      if (getOptionName(options[index]).trim().toLowerCase() === normalizedTarget) {
        return index;
      }
    }

    return -1;
  }

  function getVariantOptionValue(variant, optionIndex) {
    if (!variant || optionIndex < 0) return '';
    if (optionIndex === 0) return variant.option1 || '';
    if (optionIndex === 1) return variant.option2 || '';
    if (optionIndex === 2) return variant.option3 || '';
    return '';
  }

  function sortModels(models) {
    return models.sort(function (left, right) {
      return left.display.localeCompare(right.display, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });
  }

  function DeviceIndex(aliasResolver) {
    this.aliasResolver = aliasResolver;
    this.indexes = Object.create(null);
    this.globalDictionary = Object.create(null);
    this.globalBuilt = false;
    this.globalStats = {
      productCount: 0,
      variantCount: 0,
      modelCount: 0,
      optionFound: false,
      builtAt: null,
    };
  }

  DeviceIndex.prototype.extractGlobalDictionary = function (catalogResult, optionName) {
    var modelsMap = Object.create(null);
    var optionFound = false;
    var products = (catalogResult && catalogResult.products) || [];

    products.forEach(function (product) {
      var optionIndex = getOptionIndex(product, optionName);
      if (optionIndex < 0) return;

      optionFound = true;

      (product.variants || []).forEach(function (variant) {
        var rawValue = String(getVariantOptionValue(variant, optionIndex)).trim();
        if (!rawValue || rawValue.toLowerCase() === 'default title') return;

        var display = this.aliasResolver.normalize(rawValue);
        var normalized = this.aliasResolver.normalizeKey(display);
        if (!normalized) return;

        if (!modelsMap[normalized]) {
          modelsMap[normalized] = {
            display: display,
            normalized: normalized,
            searchKey: normalized,
            productIds: [],
            handles: [],
          };
        }

        // LCE-002: attach product membership during the same catalog pass.
        var entry = modelsMap[normalized];
        var productId = product.id;
        if (productId != null && entry.productIds.indexOf(productId) === -1) {
          entry.productIds.push(productId);
          entry.handles.push(product.handle || '');
        }
      }, this);
    }, this);

    return {
      dictionary: modelsMap,
      optionFound: optionFound,
      productCount: products.length,
      variantCount: (catalogResult && catalogResult.meta && catalogResult.meta.variantCount) || 0,
    };
  };

  DeviceIndex.prototype.isCatchAllBrand = function (brandName) {
    var normalized = this.aliasResolver.normalizeKey(brandName);
    return normalized === 'otros' || normalized === 'other' || normalized === 'others';
  };

  DeviceIndex.prototype.buildGlobalIndex = function (catalogResult, optionName, brandRecords) {
    if (debug && debug.isEnabled()) {
      debug.log(SCOPE, 'buildGlobalIndex started', {
        optionName: optionName,
        incomingProductCount: catalogResult && catalogResult.products ? catalogResult.products.length : 0,
        incomingVariantCount: catalogResult && catalogResult.meta ? catalogResult.meta.variantCount : 0,
        brandCount: (brandRecords || []).length,
      });
    }

    var extraction = this.extractGlobalDictionary(catalogResult, optionName);
    var allModels = Object.keys(extraction.dictionary).map(function (key) {
      return extraction.dictionary[key];
    });
    var assignedMap = Object.create(null);
    var enabledBrands = (brandRecords || []).filter(function (brand) {
      return brand.enabled && brand.name;
    });

    this.globalDictionary = extraction.dictionary;
    this.indexes = Object.create(null);

    enabledBrands.forEach(
      function (brand) {
        if (this.isCatchAllBrand(brand.name)) {
          return;
        }

        var brandModelsMap = Object.create(null);

        allModels.forEach(
          function (model) {
            if (this.aliasResolver.matchesBrandModel(brand.name, model.display)) {
              brandModelsMap[model.normalized] = model;
              assignedMap[model.normalized] = true;
            }
          }.bind(this)
        );

        var models = sortModels(
          Object.keys(brandModelsMap).map(function (key) {
            return brandModelsMap[key];
          })
        );

        this.indexes[brand.id] = {
          brandId: brand.id,
          brandName: brand.name,
          collectionHandle: brand.collectionHandle || '',
          models: models,
          builtAt: Date.now(),
          stats: {
            productCount: extraction.productCount,
            variantCount: extraction.variantCount,
            modelCount: models.length,
            optionFound: extraction.optionFound,
          },
        };
      }.bind(this)
    );

    enabledBrands.forEach(
      function (brand) {
        if (!this.isCatchAllBrand(brand.name)) {
          return;
        }

        var otrosModelsMap = Object.create(null);

        allModels.forEach(function (model) {
          if (!assignedMap[model.normalized]) {
            otrosModelsMap[model.normalized] = model;
          }
        });

        var otrosModels = sortModels(
          Object.keys(otrosModelsMap).map(function (key) {
            return otrosModelsMap[key];
          })
        );

        this.indexes[brand.id] = {
          brandId: brand.id,
          brandName: brand.name,
          collectionHandle: brand.collectionHandle || '',
          models: otrosModels,
          builtAt: Date.now(),
          stats: {
            productCount: extraction.productCount,
            variantCount: extraction.variantCount,
            modelCount: otrosModels.length,
            optionFound: extraction.optionFound,
          },
        };
      }.bind(this)
    );

    this.globalBuilt = true;
    this.globalStats = {
      productCount: extraction.productCount,
      variantCount: extraction.variantCount,
      modelCount: allModels.length,
      optionFound: extraction.optionFound,
      builtAt: Date.now(),
    };

    if (debug && debug.isEnabled()) {
      var brandSummary = enabledBrands.map(
        function (brand) {
          var brandIndex = this.indexes[brand.id];
          return {
            brand: brand.name,
            modelCount: brandIndex ? brandIndex.stats.modelCount : 0,
          };
        }.bind(this)
      );

      debug.log(SCOPE, 'buildGlobalIndex complete', {
        optionFound: extraction.optionFound,
        globalModelCount: allModels.length,
        productsProcessed: extraction.productCount,
        variantsProcessed: extraction.variantCount,
        brands: brandSummary,
      });

      if (!extraction.optionFound) {
        debug.warn(SCOPE, 'Configured product option not found in any product', {
          optionName: optionName,
        });
      }

      if (!allModels.length) {
        debug.warn(SCOPE, 'Zero models extracted from catalog', {
          optionName: optionName,
        });
      }
    }

    return {
      globalStats: this.globalStats,
      brandCount: enabledBrands.length,
    };
  };

  DeviceIndex.prototype.getBrandIndex = function (brandId) {
    return this.indexes[brandId] || null;
  };

  DeviceIndex.prototype.getGlobalDictionary = function () {
    return this.globalDictionary;
  };

  DeviceIndex.prototype.isGlobalBuilt = function () {
    return this.globalBuilt;
  };

  DeviceIndex.prototype.getGlobalStats = function () {
    return this.globalStats;
  };

  DeviceIndex.prototype.getBrands = function (brandRecords) {
    return (brandRecords || [])
      .filter(function (brand) {
        return brand.enabled && brand.name;
      })
      .sort(function (left, right) {
        return (left.sortOrder || 0) - (right.sortOrder || 0);
      })
      .map(function (brand) {
        var index = this.indexes[brand.id];
        return {
          id: brand.id,
          name: brand.name,
          collectionHandle: brand.collectionHandle || '',
          sortOrder: brand.sortOrder || 0,
          modelCount: index ? index.stats.modelCount : 0,
          indexed: this.globalBuilt && Boolean(index),
        };
      }, this);
  };

  DeviceIndex.prototype.getBrandModels = function (brandId) {
    var index = this.getBrandIndex(brandId);
    if (!index) return [];
    return index.models.map(function (model) {
      return model.display;
    });
  };

  /**
   * LCE-002 — resolve products for a device model from the existing dictionary.
   * @param {string} modelName
   * @returns {{ success: boolean, productIds: number[], handles: string[], model: string|null, reason: string }}
   */
  DeviceIndex.prototype.getProductsForDevice = function (modelName) {
    if (!this.globalBuilt) {
      return {
        success: false,
        productIds: [],
        handles: [],
        model: null,
        reason: 'index_missing',
      };
    }

    var normalized = this.aliasResolver.normalizeKey(this.aliasResolver.normalize(modelName));
    if (!normalized) {
      return {
        success: false,
        productIds: [],
        handles: [],
        model: null,
        reason: 'invalid_model',
      };
    }

    var entry = this.globalDictionary[normalized];
    if (!entry) {
      return {
        success: true,
        productIds: [],
        handles: [],
        model: null,
        reason: 'no_results',
      };
    }

    return {
      success: true,
      productIds: (entry.productIds || []).slice(),
      handles: (entry.handles || []).slice(),
      model: entry.display,
      reason: 'resolved',
    };
  };

  DeviceIndex.prototype.hasModel = function (brandId, modelName) {
    var normalized = this.aliasResolver.normalizeKey(modelName);
    var index = this.getBrandIndex(brandId);
    if (!index) return false;

    return index.models.some(function (model) {
      return model.normalized === normalized;
    });
  };

  DeviceIndex.prototype.normalize = function (value) {
    return this.aliasResolver.normalize(value);
  };

  DeviceIndex.prototype.searchModels = function (brandId, query, options) {
    var index = this.getBrandIndex(brandId);
    if (!index) {
      return {
        results: [],
        reason: 'index_missing',
      };
    }

    var searchMode = (options && options.searchMode) || 'contains';
    var limit = (options && options.limit) || 12;
    var normalizedQuery = this.aliasResolver.normalizeKey(query);
    var brandName = index.brandName;
    var expandedTerms = this.aliasResolver.expandSearchTerms(brandName, query);
    var normalizedTerms = expandedTerms.map(function (term) {
      return term.toLowerCase();
    });

    if (!normalizedQuery) {
      return {
        results: index.models.slice(0, limit).map(function (model) {
          return model.display;
        }),
        reason: 'all_models',
      };
    }

    var results = index.models.filter(function (model) {
      var displayLower = model.display.toLowerCase();
      var normalizedLower = model.normalized;

      if (searchMode === 'starts_with') {
        return (
          displayLower.indexOf(normalizedQuery) === 0 ||
          normalizedLower.indexOf(normalizedQuery) === 0
        );
      }

      if (searchMode === 'alias_match') {
        return normalizedTerms.some(function (term) {
          return displayLower.indexOf(term) !== -1 || normalizedLower.indexOf(term) !== -1;
        });
      }

      if (searchMode === 'normalized') {
        return normalizedLower === normalizedQuery || displayLower === String(query || '').trim().toLowerCase();
      }

      return (
        displayLower.indexOf(normalizedQuery) !== -1 ||
        normalizedLower.indexOf(normalizedQuery) !== -1 ||
        normalizedTerms.some(function (term) {
          return displayLower.indexOf(term) !== -1;
        })
      );
    });

    return {
      results: results.slice(0, limit).map(function (model) {
        return model.display;
      }),
      reason: results.length ? 'matched' : 'no_results',
    };
  };

  DeviceIndex.prototype.clear = function () {
    this.indexes = Object.create(null);
    this.globalDictionary = Object.create(null);
    this.globalBuilt = false;
    this.globalStats = {
      productCount: 0,
      variantCount: 0,
      modelCount: 0,
      optionFound: false,
      builtAt: null,
    };
  };

  LERA._modules.deviceIndex = DeviceIndex;
})(typeof window !== 'undefined' ? window : this);
